
'use client';

import React, { useEffect, useRef } from "react";
import { SEARCH_CONSTANTS } from "@/utils/searchConstants";

interface NoResultsMessageProps {
  show?: boolean;
  query?: string;
  customMessage?: string;
  onHide?: () => void; // Callback for when the message is hidden
}

const NoResultsMessage: React.FC<NoResultsMessageProps> = ({ 
  show = true,
  query,
  customMessage,
  onHide
}) => {
  const messageRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Set up auto-hide timeout when shown
  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Only set timeout if message is showing
    if (show) {
      console.log("Setting up NoResultsMessage auto-hide timeout:", SEARCH_CONSTANTS.NO_RESULTS_TIMEOUT, "ms");
      timeoutRef.current = setTimeout(() => {
        if (onHide) {
          console.log("NoResultsMessage auto-hiding after timeout");
          onHide();
        }
        timeoutRef.current = null;
      }, SEARCH_CONSTANTS.NO_RESULTS_TIMEOUT);
    }
    
    // Clean up on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [show, onHide]);
  
  // Handle clicks outside the message box
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (messageRef.current && !messageRef.current.contains(event.target as Node) && show) {
        console.log("NoResultsMessage hiding due to outside click");
        // Call the onHide callback when clicking outside
        if (onHide) onHide();
      }
    };
    
    // Add event listener when component mounts and is visible
    if (show) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    
    // Clean up event listener when component unmounts or hides
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [show, onHide]);
  
  // Only render when show is true
  if (!show) return null;
  
  const displayMessage = customMessage || 
    (query ? `No movies found for "${query}"` : "No movies found");
  
  return (
    <div 
      ref={messageRef}
      className="absolute mt-2 w-full max-w-2xl left-0 bg-mono-eerieBlack border border-mono-slateGray/30 rounded-md z-50 flex items-center justify-center py-3"
    >
      <p className="text-mono-frenchGray">
        {displayMessage}
      </p>
    </div>
  );
};

export default NoResultsMessage;
